import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import axios from '../../utils/api';
import { motion } from 'framer-motion';
import {
  Settings,
  User,
  Shield,
  Bell,
  Palette,
  Database,
  Mail,
  Globe,
  Lock,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info,
  Sun,
  Moon,
  Monitor,
  Users,
  FileText,
  HardDrive,
  Activity
} from 'lucide-react';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const { user } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    // General Settings
    siteName: 'KirBOO Flowsafe Admin Portal',
    siteDescription: 'Lead management and admin portal for KirBOO Flowsafe Pvt Ltd',
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    currency: 'INR',
    
    // Notification Settings
    emailNotifications: true,
    leadNotifications: true,
    systemAlerts: true,
    weeklyReports: true,
    
    // Security Settings
    sessionTimeout: 30,
    passwordExpiry: 90,
    maxLoginAttempts: 5,
    twoFactorAuth: false,
    
    // System Settings
    maintenanceMode: false,
    debugMode: false,
    logLevel: 'info',
    backupFrequency: 'daily',
    
    // Email Settings
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpSecure: true,
    
    // Lead Settings
    autoAssignment: true,
    leadRetention: 365,
    duplicateDetection: true,
    leadScoring: false
  });

  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    totalLeads: 0,
    storageUsed: '0 MB',
    lastBackup: 'Never',
    uptime: '0 days'
  });

  useEffect(() => {
    fetchSettings();
    fetchSystemStats();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      // For now, we'll use default settings
      // In a real implementation, this would fetch from the backend
      console.log('Settings loaded from defaults');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStats = async () => {
    try {
      // Mock system stats - in real implementation, fetch from backend
      setSystemStats({
        totalUsers: 5,
        totalLeads: 150,
        storageUsed: '45.2 MB',
        lastBackup: '2 hours ago',
        uptime: '15 days'
      });
    } catch (error) {
      console.error('Failed to fetch system stats:', error);
    }
  };

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async () => {
    setSaveLoading(true);
    try {
      // In a real implementation, this would save to backend
      await new Promise(resolve => setTimeout(resolve, 1000)); // Mock delay
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaveLoading(false);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'system', name: 'System', icon: Database },
    { id: 'email', name: 'Email', icon: Mail },
    { id: 'leads', name: 'Leads', icon: Users }
  ];

  const SettingCard = ({ title, description, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      {children}
    </div>
  );

  const Toggle = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const InputField = ({ label, type = 'text', value, onChange, placeholder, description }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
      />
      {description && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{description}</p>
      )}
    </div>
  );

  const SelectField = ({ label, value, onChange, options, description }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {description && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{description}</p>
      )}
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      {/* <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
          <Settings className="w-8 h-8 mr-3" />
          System Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Configure and manage system-wide settings and preferences
        </p>
      </div> */}

      {/* System Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div className="ml-3">
              <p className="text-sm text-blue-600 dark:text-blue-400">Total Users</p>
              <p className="text-xl font-semibold text-blue-900 dark:text-blue-100">{systemStats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-green-600 dark:text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-600 dark:text-green-400">Total Leads</p>
              <p className="text-xl font-semibold text-green-900 dark:text-green-100">{systemStats.totalLeads}</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <div className="flex items-center">
            <HardDrive className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <div className="ml-3">
              <p className="text-sm text-purple-600 dark:text-purple-400">Storage Used</p>
              <p className="text-xl font-semibold text-purple-900 dark:text-purple-100">{systemStats.storageUsed}</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-center">
            <Database className="w-8 h-8 text-orange-600 dark:text-orange-400" />
            <div className="ml-3">
              <p className="text-sm text-orange-600 dark:text-orange-400">Last Backup</p>
              <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">{systemStats.lastBackup}</p>
            </div>
          </div>
        </div>

        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            <div className="ml-3">
              <p className="text-sm text-indigo-600 dark:text-indigo-400">System Uptime</p>
              <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">{systemStats.uptime}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Settings Tabs */}
        <div className="lg:w-1/4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:w-3/4">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* General Settings */}
            {activeTab === 'general' && (
              <>
                <SettingCard
                  title="Site Information"
                  description="Basic information about your admin portal"
                >
                  <InputField
                    label="Site Name"
                    value={settings.siteName}
                    onChange={(value) => handleSettingChange('general', 'siteName', value)}
                    placeholder="Enter site name"
                  />
                  <InputField
                    label="Site Description"
                    value={settings.siteDescription}
                    onChange={(value) => handleSettingChange('general', 'siteDescription', value)}
                    placeholder="Enter site description"
                  />
                </SettingCard>

                <SettingCard
                  title="Regional Settings"
                  description="Configure timezone, date format, and currency"
                >
                  <SelectField
                    label="Timezone"
                    value={settings.timezone}
                    onChange={(value) => handleSettingChange('general', 'timezone', value)}
                    options={[
                      { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
                      { value: 'UTC', label: 'UTC' },
                      { value: 'America/New_York', label: 'America/New_York (EST)' },
                      { value: 'Europe/London', label: 'Europe/London (GMT)' }
                    ]}
                  />
                  <SelectField
                    label="Date Format"
                    value={settings.dateFormat}
                    onChange={(value) => handleSettingChange('general', 'dateFormat', value)}
                    options={[
                      { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                      { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                      { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
                    ]}
                  />
                  <SelectField
                    label="Currency"
                    value={settings.currency}
                    onChange={(value) => handleSettingChange('general', 'currency', value)}
                    options={[
                      { value: 'INR', label: 'Indian Rupee (₹)' },
                      { value: 'USD', label: 'US Dollar ($)' },
                      { value: 'EUR', label: 'Euro (€)' },
                      { value: 'GBP', label: 'British Pound (£)' }
                    ]}
                  />
                </SettingCard>

                <SettingCard
                  title="Theme Settings"
                  description="Customize the appearance of the admin portal"
                >
                  <div className="flex items-center justify-between py-3">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Dark Mode
                      </label>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Toggle between light and dark theme
                      </p>
                    </div>
                    <button
                      onClick={toggleTheme}
                      className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      {isDarkMode ? (
                        <Sun className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <Moon className="w-5 h-5 text-blue-500" />
                      )}
                    </button>
                  </div>
                </SettingCard>
              </>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <>
                <SettingCard
                  title="Email Notifications"
                  description="Configure when to send email notifications"
                >
                  <Toggle
                    checked={settings.emailNotifications}
                    onChange={(value) => handleSettingChange('notifications', 'emailNotifications', value)}
                    label="Enable Email Notifications"
                    description="Receive notifications via email"
                  />
                  <Toggle
                    checked={settings.leadNotifications}
                    onChange={(value) => handleSettingChange('notifications', 'leadNotifications', value)}
                    label="New Lead Notifications"
                    description="Get notified when new leads are created"
                  />
                  <Toggle
                    checked={settings.systemAlerts}
                    onChange={(value) => handleSettingChange('notifications', 'systemAlerts', value)}
                    label="System Alerts"
                    description="Receive alerts about system issues"
                  />
                  <Toggle
                    checked={settings.weeklyReports}
                    onChange={(value) => handleSettingChange('notifications', 'weeklyReports', value)}
                    label="Weekly Reports"
                    description="Receive weekly summary reports"
                  />
                </SettingCard>
              </>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <>
                <SettingCard
                  title="Session & Authentication"
                  description="Configure security and authentication settings"
                >
                  <InputField
                    label="Session Timeout (minutes)"
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(value) => handleSettingChange('security', 'sessionTimeout', parseInt(value))}
                    description="Automatically log out users after this period of inactivity"
                  />
                  <InputField
                    label="Password Expiry (days)"
                    type="number"
                    value={settings.passwordExpiry}
                    onChange={(value) => handleSettingChange('security', 'passwordExpiry', parseInt(value))}
                    description="Force users to change password after this period"
                  />
                  <InputField
                    label="Max Login Attempts"
                    type="number"
                    value={settings.maxLoginAttempts}
                    onChange={(value) => handleSettingChange('security', 'maxLoginAttempts', parseInt(value))}
                    description="Lock account after this many failed login attempts"
                  />
                  <Toggle
                    checked={settings.twoFactorAuth}
                    onChange={(value) => handleSettingChange('security', 'twoFactorAuth', value)}
                    label="Two-Factor Authentication"
                    description="Require additional verification for login"
                  />
                </SettingCard>
              </>
            )}

            {/* System Settings */}
            {activeTab === 'system' && (
              <>
                <SettingCard
                  title="System Configuration"
                  description="Advanced system settings and maintenance options"
                >
                  <Toggle
                    checked={settings.maintenanceMode}
                    onChange={(value) => handleSettingChange('system', 'maintenanceMode', value)}
                    label="Maintenance Mode"
                    description="Put the system in maintenance mode"
                  />
                  <Toggle
                    checked={settings.debugMode}
                    onChange={(value) => handleSettingChange('system', 'debugMode', value)}
                    label="Debug Mode"
                    description="Enable detailed logging for troubleshooting"
                  />
                  <SelectField
                    label="Log Level"
                    value={settings.logLevel}
                    onChange={(value) => handleSettingChange('system', 'logLevel', value)}
                    options={[
                      { value: 'error', label: 'Error' },
                      { value: 'warn', label: 'Warning' },
                      { value: 'info', label: 'Info' },
                      { value: 'debug', label: 'Debug' }
                    ]}
                    description="Set the minimum log level to record"
                  />
                  <SelectField
                    label="Backup Frequency"
                    value={settings.backupFrequency}
                    onChange={(value) => handleSettingChange('system', 'backupFrequency', value)}
                    options={[
                      { value: 'hourly', label: 'Hourly' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                      { value: 'monthly', label: 'Monthly' }
                    ]}
                    description="How often to create system backups"
                  />
                </SettingCard>
              </>
            )}

            {/* Email Settings */}
            {activeTab === 'email' && (
              <>
                <SettingCard
                  title="SMTP Configuration"
                  description="Configure email server settings for sending notifications"
                >
                  <InputField
                    label="SMTP Host"
                    value={settings.smtpHost}
                    onChange={(value) => handleSettingChange('email', 'smtpHost', value)}
                    placeholder="smtp.gmail.com"
                    description="SMTP server hostname"
                  />
                  <InputField
                    label="SMTP Port"
                    type="number"
                    value={settings.smtpPort}
                    onChange={(value) => handleSettingChange('email', 'smtpPort', parseInt(value))}
                    description="SMTP server port (usually 587 for TLS or 465 for SSL)"
                  />
                  <InputField
                    label="SMTP Username"
                    value={settings.smtpUsername}
                    onChange={(value) => handleSettingChange('email', 'smtpUsername', value)}
                    placeholder="your-email@example.com"
                    description="SMTP authentication username"
                  />
                  <InputField
                    label="SMTP Password"
                    type="password"
                    value={settings.smtpPassword}
                    onChange={(value) => handleSettingChange('email', 'smtpPassword', value)}
                    description="SMTP authentication password"
                  />
                  <Toggle
                    checked={settings.smtpSecure}
                    onChange={(value) => handleSettingChange('email', 'smtpSecure', value)}
                    label="Use Secure Connection"
                    description="Enable TLS/SSL encryption"
                  />
                </SettingCard>
              </>
            )}

            {/* Lead Settings */}
            {activeTab === 'leads' && (
              <>
                <SettingCard
                  title="Lead Management"
                  description="Configure how leads are handled and processed"
                >
                  <Toggle
                    checked={settings.autoAssignment}
                    onChange={(value) => handleSettingChange('leads', 'autoAssignment', value)}
                    label="Auto-assign Leads"
                    description="Automatically assign new leads to available agents"
                  />
                  <InputField
                    label="Lead Retention Period (days)"
                    type="number"
                    value={settings.leadRetention}
                    onChange={(value) => handleSettingChange('leads', 'leadRetention', parseInt(value))}
                    description="How long to keep lead data before archiving"
                  />
                  <Toggle
                    checked={settings.duplicateDetection}
                    onChange={(value) => handleSettingChange('leads', 'duplicateDetection', value)}
                    label="Duplicate Detection"
                    description="Automatically detect and flag duplicate leads"
                  />
                  <Toggle
                    checked={settings.leadScoring}
                    onChange={(value) => handleSettingChange('leads', 'leadScoring', value)}
                    label="Lead Scoring"
                    description="Enable automatic lead scoring based on criteria"
                  />
                </SettingCard>
              </>
            )}
          </motion.div>

          {/* Save Button */}
          <div className="mt-8 flex justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSaveSettings}
              disabled={saveLoading}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saveLoading ? (
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Save className="w-5 h-5 mr-2" />
              )}
              {saveLoading ? 'Saving...' : 'Save Settings'}
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
